import { useState } from "react"
import type { PlanModality } from "@/services/types"
import { cn } from "@/shared/lib/cn"
import { openWhatsApp } from "@/shared/lib/whatsapp"
import { useInView } from "@/shared/hooks/useInView"
import { Disclaimer } from "@/shared/ui/Disclaimer"
import { Reveal } from "@/shared/ui/Reveal"
import { Section } from "@/shared/ui/Section"
import { SectionHeading } from "@/shared/ui/SectionHeading"
import { ArrowRightSm, ChevronDown, InfoCircle, ListRows } from "@/shared/ui/icons"
import { ModalityTabs } from "@/features/plans/components/ModalityTabs"
import { PlanCard } from "@/features/plans/components/PlanCard"
import { PlanComparisonTable } from "@/features/plans/components/PlanComparisonTable"
import { usePlans } from "@/features/plans/usePlans"

const HEADING_ID = "plans-heading"

export function PlansSection() {
  const [modality, setModality] = useState<PlanModality>("flex")
  const [compareOpen, setCompareOpen] = useState(false)
  const { ref, inView } = useInView()

  const { plans, comparison } = usePlans(modality)

  return (
    <Section ref={ref} id="planos" tone="off-white" aria-labelledby={HEADING_ID}>
      <Reveal inView={inView} className="mb-12 max-w-2xl">
        <SectionHeading
          titleId={HEADING_ID}
          eyebrow="Planos DIMPET"
          title={
            <>
              Escolha a cobertura ideal
              <br />
              <span className="shimmer-gold">para o seu pet</span>
            </>
          }
        />
        <p className="text-base text-verde-profundo/55 leading-relaxed max-w-lg mt-4">
          Dois modelos de planos: <strong className="text-verde-profundo">Cobertura Total</strong> sem
          coparticipação, ou <strong className="text-verde-profundo">Flex</strong> com coparticipação orientada e
          mensalidade reduzida.
        </p>
      </Reveal>

      <Reveal inView={inView} delayMs={100} className="mb-10">
        <ModalityTabs value={modality} onChange={setModality} />
      </Reveal>

      {modality === "flex" && (
        <div className="mb-8 flex items-start gap-3 p-4 rounded-xl bg-dourado/8 border border-dourado/20 animate-fade-in max-w-2xl">
          <span className="shrink-0 mt-0.5 text-dourado-escuro">
            <InfoCircle />
          </span>
          <p className="text-xs text-verde-profundo/65 leading-relaxed">
            <strong className="text-verde-profundo">Planos Flex:</strong> mensalidade reduzida com coparticipação
            por procedimento. Você paga uma parte do custo de cada atendimento conforme a tabela orientada do
            plano.
          </p>
        </div>
      )}

      {plans.error ? (
        <p role="alert" className="text-sm text-verde-profundo/60">
          Não foi possível carregar os planos agora.{" "}
          <button type="button" onClick={plans.reload} className="underline font-semibold cursor-pointer">
            Tentar novamente
          </button>
        </p>
      ) : (
        <div
          className={cn(
            "grid gap-5",
            modality === "cobertura-total"
              ? "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              : "sm:grid-cols-2 lg:grid-cols-3",
          )}
        >
          {(plans.data ?? []).map((plan, index) => (
            <Reveal key={plan.id} inView={inView} delayMs={index * 90} distance="lg" className="h-full">
              <PlanCard plan={plan} />
            </Reveal>
          ))}
        </div>
      )}

      <div className="mt-10 flex justify-center">
        <button
          type="button"
          onClick={() => setCompareOpen((open) => !open)}
          aria-expanded={compareOpen}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-verde-profundo/15 text-sm font-medium text-verde-profundo/60 hover:text-verde-profundo hover:border-verde-profundo/28 transition-all bg-white shadow-sm cursor-pointer"
        >
          <ListRows />
          {compareOpen ? "Ocultar comparativo" : "Comparar todos os planos"}
          <ChevronDown size={12} className={cn("transition-transform duration-200", compareOpen && "rotate-180")} />
        </button>
      </div>

      {compareOpen && comparison.data && (
        <div className="mt-6 bg-white rounded-2xl p-6 lg:p-8 border border-verde-profundo/10 shadow-sm animate-fade-in">
          <p className="text-sm font-semibold text-verde-profundo mb-6">
            Comparativo — {modality === "cobertura-total" ? "Cobertura Total" : "Flex com Coparticipação"}
          </p>

          <PlanComparisonTable comparison={comparison.data} />

          <Disclaimer size="xs" className="mt-6 pt-4">
            Valores e limites sujeitos a condições contratuais e carências conforme regulamento do plano.
            Coparticipação (copag.) incide sobre o valor orientado de cada procedimento na tabela do plano.
          </Disclaimer>
        </div>
      )}

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-verde-profundo/8">
        <p className="text-xs text-verde-profundo/40 leading-relaxed max-w-md">
          Todos os planos cobrem cães e gatos. Carências, limites e coberturas conforme regulamento contratual.
          Consulte as condições completas antes de contratar.
        </p>

        <button
          type="button"
          onClick={() => openWhatsApp()}
          className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-dourado text-white text-sm font-semibold hover:bg-[#b8842e] transition-colors shadow-md cursor-pointer"
        >
          Falar com especialista
          <ArrowRightSm />
        </button>
      </div>
    </Section>
  )
}
